import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/utils/utils";

const notificationVariants = cva(
  "w-full box-border rounded-md px-4 py-3 text-sm break-words overflow-hidden",
  {
    variants: {
      intent: {
        error:
          "border border-red-500/40 bg-red-500/10 text-red-200",
        success:
          "border border-green-500/40 bg-green-500/10 text-green-200",
        warning:
          "border border-yellow-500/40 bg-yellow-500/10 text-yellow-200",
        info:
          "border border-blue-500/40 bg-blue-500/10 text-blue-200",
      },
      size: {
        sm: "px-3 py-2 text-[13px]",
        md: "",
        lg: "px-5 py-4 text-base",
      },
    },
    defaultVariants: {
      intent: "info",
      size: "md",
    },
  }
);

export interface NotificationRowProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof notificationVariants> {
  asChild?: boolean;
  role?: React.AriaRole;
  "aria-live"?: React.AriaAttributes["aria-live"];
}

const NotificationRow = React.forwardRef<HTMLDivElement, NotificationRowProps>(
  (
    {
      asChild,
      className,
      intent,
      size,
      role: roleProp,
      "aria-live": ariaLiveProp,
      ...props
    },
    ref
  ) => {
    const role =
      roleProp ??
      (intent === "error" ? "alert" : "status");

    const ariaLive =
      ariaLiveProp ??
      (intent === "error" ? "assertive" : "polite");

    const Comp = asChild ? Slot : "div";

    return (
      <Comp
        ref={ref}
        role={role}
        aria-live={ariaLive}
        className={cn(notificationVariants({ intent, size }), className)}
        {...props}
      />
    );
  }
);
NotificationRow.displayName = "NotificationRow";

export { NotificationRow, notificationVariants };

